import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import {
  VITALS_GRID_COLUMNS,
  VITALS_GRID_ROWS,
} from './thirtyDaySummaryConfig';
import type { SectionDef } from './thirtyDaySummaryConfig';

interface SummaryFieldRendererProps {
  section: SectionDef;
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}

const OTHER_CHECKBOX_OPTION = 'Other';

interface CheckboxGroupSectionProps {
  section: SectionDef;
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}

function CheckboxGroupSection({
  section,
  control,
  register,
  setValue,
}: CheckboxGroupSectionProps) {
  const selected =
    useWatch({ control, name: section.key, defaultValue: [] }) ?? [];
  const selectedValues = Array.isArray(selected) ? selected : [];
  const showOtherInput =
    section.allowOther && selectedValues.includes(OTHER_CHECKBOX_OPTION);

  return (
    <div className='space-y-1.5'>
      <Label>{section.label}</Label>
      <Controller
        name={section.key}
        control={control}
        render={({ field }) => {
          const current = (field.value as string[] | undefined) ?? [];
          const toggle = (option: string, checked: boolean) => {
            if (checked) {
              if (!current.includes(option)) {
                field.onChange([...current, option]);
              }
              return;
            }
            field.onChange(current.filter(v => v !== option));
            if (option === OTHER_CHECKBOX_OPTION && section.allowOther) {
              setValue(`${section.key}_other`, '', { shouldDirty: true });
            }
          };
          const inline = section.checkboxLayout === 'inline';
          const maxColumns = section.checkboxMaxColumns;
          const wrapContainerClass = maxColumns
            ? maxColumns >= 3
              ? 'grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3'
              : maxColumns === 2
                ? 'grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2'
                : 'grid grid-cols-1 gap-x-6 gap-y-2'
            : 'flex flex-wrap gap-x-6 gap-y-2';
          const wrapLabelClass = 'flex items-start gap-2 text-sm leading-snug';
          return (
            <div
              className={
                inline
                  ? 'flex flex-wrap items-center gap-x-4 gap-y-1'
                  : wrapContainerClass
              }
            >
              {(section.options ?? []).map(option => (
                <label
                  key={option}
                  className={
                    inline
                      ? 'inline-flex shrink-0 items-center gap-1.5 text-sm'
                      : wrapLabelClass
                  }
                >
                  <Checkbox
                    checkboxSize='sm'
                    className={inline ? 'shrink-0' : 'mt-0.5 shrink-0'}
                    checked={current.includes(option)}
                    onCheckedChange={checked =>
                      toggle(option, Boolean(checked))
                    }
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          );
        }}
      />
      {showOtherInput && (
        <Input
          {...register(`${section.key}_other`)}
          inputSize='sm'
          placeholder='Other (please specify)'
        />
      )}
    </div>
  );
}

// Renders summary form sections by field kind — a small switch, NOT a generic
// schema-driven form engine. Text fields use `register`; non-text widgets use `Controller` so RHF owns their value.
// Styling uses @roger/ui-components size props (no className height/padding).
export function SummaryFieldRenderer({
  section,
  control,
  register,
  setValue,
}: SummaryFieldRendererProps) {
  switch (section.kind) {
    case 'textarea':
      return (
        <div className='space-y-1.5'>
          <Label>{section.label}</Label>
          <Textarea
            {...register(section.key)}
            placeholder={section.placeholder ?? ''}
            textareaSize='sm'
            rows={4}
          />
        </div>
      );

    case 'single-select':
      return (
        <div className='space-y-1.5'>
          <Label>{section.label}</Label>
          <Controller
            name={section.key}
            control={control}
            render={({ field }) => (
              <Select
                value={(field.value as string) ?? ''}
                onValueChange={field.onChange}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue
                    placeholder={section.placeholder ?? 'Select...'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(section.options ?? []).map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      );

    case 'checkbox-group':
      return (
        <CheckboxGroupSection
          section={section}
          control={control}
          register={register}
          setValue={setValue}
        />
      );

    case 'vitals-grid':
      return (
        <div className='space-y-1.5'>
          <Label>{section.label}</Label>
          <div className='flex flex-col gap-2'>
            <div className='grid grid-cols-9 gap-2'>
              <span />
              {VITALS_GRID_COLUMNS.map(col => (
                <span key={col.key} className='text-xs text-muted-foreground'>
                  {col.label}
                </span>
              ))}
            </div>
            {VITALS_GRID_ROWS.map(row => (
              <div key={row} className='grid grid-cols-9 items-center gap-2'>
                <span className='text-xs text-muted-foreground'>{row}</span>
                {VITALS_GRID_COLUMNS.map(col => (
                  <Input
                    key={col.key}
                    {...register(`${section.key}.${row}.${col.key}`)}
                    inputSize='sm'
                    type='text'
                    inputMode='decimal'
                    aria-label={`${row} ${col.label}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return null;
  }
}
